// @ts-check
// Cliente de servicio para la ejecución efímera de un diagrama
// (CAP-EDITOR-EXECUTION, TSK-0058, DEC-025, docs/03-frontend.md §9.1). Como
// todo servicio, no toca el DOM y usa exclusivamente `apiRequest` de
// `api-client.js` -- nunca `fetch` directamente.
//
// Consume `POST /api/v1/diagrams/execute`
// (backend/src/app/routers/v1/diagram_execution.py,
// backend/src/app/schemas/diagram_execution.py). El cuerpo enviado es
// `{ programa, entradas }`: `programa` es la IR-JSON producida por
// `frontend/src/features/diagram-editor/ir-serialization.js#serializarPrograma`
// tal cual (sin `id`/posición/color/tamaño, DEC-025) y `entradas` es la lista
// ordenada de valores simulados que consumirán, en orden, los nodos
// `Entrada` del programa (quien decide ESE orden es `execution-panel.js`,
// no este cliente: este módulo solo transporta la lista ya construida).
//
// El mapeo de campos de este módulo se limita a los que
// `RespuestaEjecucionDiagrama` (backend/src/app/schemas/diagram_execution.py)
// declara realmente: `exito`, `variables_finales`, `salidas`,
// `salidas_tras_ultima_entrada`, `pasos_ejecutados`, `limite_alcanzado`,
// `error`, `necesita_entrada`, `variable_pendiente`. No se inventan campos
// que ese schema no declare (p.ej. nada de `traza`, deliberadamente fuera de
// esa respuesta, ver docstring de ese schema).

import { apiRequest } from './api-client.js';

/** @typedef {number|string|boolean|null|Array<unknown>} ValorEjecucion */

/**
 * @typedef {{
 *   exito: boolean,
 *   variablesFinales: Record<string, ValorEjecucion>,
 *   salidas: ValorEjecucion[],
 *   salidasTrasUltimaEntrada: ValorEjecucion[],
 *   pasosEjecutados: number,
 *   limiteAlcanzado: boolean,
 *   error: string|null,
 *   necesitaEntrada: boolean,
 *   variablePendiente: string|null,
 * }} ResultadoEjecucionDiagrama
 */

/**
 * Mapea la respuesta cruda de `POST /diagrams/execute`
 * (`RespuestaEjecucionDiagrama`, snake_case) al tipo interno de frontend
 * (camelCase). Deliberadamente defensivo con `unknown`: no confía en que la
 * red entregue exactamente la forma esperada (mismo criterio que
 * `exercise-client.js#mapearEjercicioPublico`).
 * @param {unknown} datos
 * @returns {ResultadoEjecucionDiagrama}
 */
export function mapearResultadoEjecucion(datos) {
  const crudo = /** @type {Record<string, unknown>} */ (datos ?? {});
  const variablesCrudas = /** @type {Record<string, unknown>} */ (crudo.variables_finales ?? {});
  return {
    exito: Boolean(crudo.exito),
    variablesFinales: /** @type {Record<string, ValorEjecucion>} */ (variablesCrudas),
    salidas: Array.isArray(crudo.salidas) ? /** @type {ValorEjecucion[]} */ (crudo.salidas) : [],
    // Degradación DELIBERADA a `salidas` (no a `[]`) si el campo no viene:
    // ante una respuesta antigua/parcial, quien puntúa acaba comparando la
    // secuencia íntegra DOS veces, es decir, se comporta exactamente como
    // antes de existir este campo. Con `[]` en cambio se "aceptaría de más"
    // (una lista vacía coincidiría con una salida esperada vacía), que es
    // justo la dirección peligrosa: aprobar algo que no lo merece.
    salidasTrasUltimaEntrada: Array.isArray(crudo.salidas_tras_ultima_entrada)
      ? /** @type {ValorEjecucion[]} */ (crudo.salidas_tras_ultima_entrada)
      : Array.isArray(crudo.salidas)
        ? /** @type {ValorEjecucion[]} */ (crudo.salidas)
        : [],
    pasosEjecutados: typeof crudo.pasos_ejecutados === 'number' ? crudo.pasos_ejecutados : 0,
    limiteAlcanzado: Boolean(crudo.limite_alcanzado),
    error: typeof crudo.error === 'string' ? crudo.error : null,
    // El programa se paró esperando un dato. Degradación a `false` si el
    // campo no viene (respuesta antigua): el panel enseña el error tal cual,
    // que es exactamente como se comportaba antes de existir este campo --
    // nunca se queda pidiendo datos en un bucle contra un servidor que no
    // sabe responderle.
    necesitaEntrada: Boolean(crudo.necesita_entrada),
    variablePendiente:
      typeof crudo.variable_pendiente === 'string' ? crudo.variable_pendiente : null,
  };
}

/**
 * Ejecuta de forma efímera (DEC-025: sin persistencia ni autenticación) el
 * programa `programa` (IR-JSON tal cual la produce `serializarPrograma`)
 * contra `POST /api/v1/diagrams/execute`, con `entradas` como valores
 * simulados en el mismo orden en que los consumirán los nodos `Entrada` del
 * programa. Propaga `ApiError` (red/timeout/422/500, ver `api-errors.js`)
 * sin capturarla: esta capa de servicio no decide cómo se muestra el error,
 * eso es responsabilidad de quien la llama (`execution-panel.js`).
 * @param {{ programa: Record<string, unknown>, entradas?: ValorEjecucion[], signal?: AbortSignal }} opciones
 * @returns {Promise<ResultadoEjecucionDiagrama>}
 */
export async function ejecutarDiagrama(opciones) {
  const { programa, entradas = [], signal } = opciones ?? /** @type {any} */ ({});
  if (typeof programa !== 'object' || programa === null || Array.isArray(programa)) {
    throw new TypeError('ejecutarDiagrama: "programa" debe ser un objeto IR-JSON (nodo Programa serializado)');
  }
  if (!Array.isArray(entradas)) {
    throw new TypeError('ejecutarDiagrama: "entradas" debe ser un array de valores simulados');
  }
  const datos = await apiRequest('/diagrams/execute', {
    method: 'POST',
    body: { programa, entradas },
    signal,
  });
  return mapearResultadoEjecucion(datos);
}
